import { getInputProps, type FieldMetadata } from '@conform-to/react';

import { Input, type InputProps } from '~/components/ui/input.tsx';
import { Label, type LabelProps } from '~/components/ui/label.tsx';
import { cn } from '~/styles/extended-theme.ts';

import { FieldErrors } from './form-errors.tsx';

// subset of types supported by this field
type TextInputType = 'text' | 'email' | 'password' | 'number' | 'tel' | 'url';

export interface TextInputProps<T> {
  field: FieldMetadata<T>;
  label?: string;
  withAsterisk?: boolean;
  characterCount?: number;
  placeholder?: string;
  className?: string;
  inputProps?: Omit<InputProps, 'id' | 'name' | 'aria-invalid' | 'aria-describedby' | 'type'> & {
    type?: TextInputType | undefined;
  };
  labelProps?: Omit<LabelProps, 'htmlFor'>;
  disabled?: boolean;
  prefix?: string;
  suffix?: string;
  type?: TextInputType;
}

export function TextField<T>({
  field,
  label,
  withAsterisk,
  characterCount,
  placeholder,
  className,
  inputProps,
  labelProps,
  disabled = false,
  prefix,
  suffix,
  type = inputProps?.type ?? 'text',
}: TextInputProps<T>) {
  const hasError = Boolean(field?.errors?.length);

  const input = (
    <Input
      placeholder={placeholder}
      hasError={hasError}
      disabled={disabled}
      {...inputProps}
      {...getInputProps(field, { type })}
    />
  );

  return (
    <div className={cn('grid w-full items-center gap-2 pb-2', disabled && 'opacity-50', className)}>
      {label ? (
        <Label className="font-bold" htmlFor={field.id} hasError={hasError} {...labelProps}>
          {label} {withAsterisk ? <span>*</span> : null}
        </Label>
      ) : null}
      {prefix || suffix ? (
        <div className="flex">
          {prefix ? <span className="mr-1 mt-2 text-sm">{prefix}</span> : null}
          {input}
          {suffix ? <span className="ml-1 mt-2 text-sm">{suffix}</span> : null}
        </div>
      ) : (
        input
      )}
      {characterCount ? (
        <span className="text-caption">
          {typeof field.value === 'string' ? field.value.length : 0} / {characterCount}
        </span>
      ) : null}
      <div className="px-2 pb-3 pt-1">
        {field.errorId ? <FieldErrors id={field.errorId} errors={field.errors} /> : null}
      </div>
    </div>
  );
}
